import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Globe,
  Settings,
  ShieldAlert,
  Upload,
  X,
  ImageIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ClubPicker } from "@/components/ClubPicker";
import { resolveAssetUrl } from "@/lib/assets";
import {
  useGetLeagueDetail,
  useUpdateLeague,
  getGetLeagueDetailQueryKey,
} from "@workspace/api-client-react";

type ProfileLike = {
  country?: string | null;
  homeClubId?: string | null;
} | null | undefined;

export function EditLeaguePage() {
  const params = useParams<{ id: string }>();
  const leagueId = params.id ?? "";
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const profile = user?.profile as ProfileLike;
  const profileCountry = profile?.country || null;

  const { data, isLoading } = useGetLeagueDetail(leagueId, {
    query: {
      queryKey: getGetLeagueDetailQueryKey(leagueId),
      enabled: !!leagueId,
    },
  });

  const league = data?.league;
  const role = data?.userMembership?.role;

  const [form, setForm] = useState({
    name: "",
    description: "",
    isPrivate: true,
    matchFormat: "doubles" as "singles" | "doubles",
    homeClubId: null as string | null,
    imageUrl: "" as string,
  });
  const [hydrated, setHydrated] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (league && !hydrated) {
      setForm({
        name: league.name,
        description: league.description || "",
        isPrivate: league.isPrivate,
        matchFormat:
          (league.matchFormat as "singles" | "doubles") || "doubles",
        homeClubId: league.homeClubId || null,
        imageUrl: league.imageUrl || "",
      });
      setHydrated(true);
    }
  }, [league, hydrated]);

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch("/api/upload/league-image", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) throw new Error("upload failed");
      const json = await res.json();
      setForm((f) => ({ ...f, imageUrl: json.url }));
    } catch {
      toast({
        title: "Error",
        description: "No se pudo subir la imagen",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const canEdit = role === "owner" || role === "admin";

  const update = useUpdateLeague({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
        queryClient.invalidateQueries({
          queryKey: getGetLeagueDetailQueryKey(leagueId),
        });
        toast({
          title: "Liga actualizada",
          description: "Los cambios se han guardado.",
        });
        setLocation(`/league/${leagueId}`);
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "No se pudo actualizar la liga";
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      },
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data?.league) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Liga no encontrada</p>
        <Button variant="outline" onClick={() => history.back()}>
          Volver
        </Button>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-10 w-10 text-destructive" />
        <p className="text-sm">No tienes permisos para editar esta liga.</p>
        <Button variant="outline" onClick={() => history.back()}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Settings className="h-4 w-4 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Editar liga</h1>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 py-4 safe-area-bottom">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({
              id: leagueId,
              data: {
                name: form.name,
                description: form.description || null,
                isPrivate: form.isPrivate,
                matchFormat: form.matchFormat,
                homeClubId: form.homeClubId,
                imageUrl: form.imageUrl || null,
              },
            });
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="input-edit-league-name"
            />
          </div>

          <div className="space-y-2">
            <Label>Imagen de la liga</Label>
            <div className="flex items-center gap-2">
              <label
                htmlFor="edit-league-image-upload"
                className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-muted/50"
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm text-muted-foreground">
                      Subiendo...
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {form.imageUrl ? "Cambiar imagen" : "Subir imagen"}
                    </span>
                  </>
                )}
                <input
                  id="edit-league-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  data-testid="input-edit-league-image-upload"
                />
              </label>
              {form.imageUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setForm({ ...form, imageUrl: "" })}
                  data-testid="button-remove-edit-league-image"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {form.imageUrl ? (
              <div className="mt-2 rounded-lg overflow-hidden border">
                <img
                  src={resolveAssetUrl(form.imageUrl)}
                  alt="Preview"
                  className="w-full h-24 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            ) : (
              <div className="mt-2 rounded-lg border border-dashed h-16 flex items-center justify-center text-muted-foreground">
                <ImageIcon className="h-5 w-5 mr-2" />
                <span className="text-xs">Sin imagen</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={3}
              data-testid="input-edit-league-description"
            />
          </div>

          <div className="space-y-2">
            <Label>Formato</Label>
            <Select
              value={form.matchFormat}
              onValueChange={(v) =>
                setForm({ ...form, matchFormat: v as "singles" | "doubles" })
              }
            >
              <SelectTrigger data-testid="select-edit-match-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="doubles">Dobles</SelectItem>
                <SelectItem value="singles">Singles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Club sede (opcional)</Label>
            <ClubPicker
              value={form.homeClubId}
              onChange={(id) => setForm({ ...form, homeClubId: id })}
              country={profileCountry}
              testId="edit-league-club-picker"
            />
            <p className="text-xs text-muted-foreground">
              Se usará como club por defecto al registrar partidos de esta liga.
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              {form.isPrivate ? (
                <Lock className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Globe className="h-4 w-4 text-muted-foreground" />
              )}
              <div>
                <Label htmlFor="private-edit" className="font-medium">
                  Liga privada
                </Label>
                <p className="text-xs text-muted-foreground">
                  {form.isPrivate
                    ? "Solo miembros invitados pueden unirse"
                    : "Cualquiera puede solicitar unirse"}
                </p>
              </div>
            </div>
            <Switch
              id="private-edit"
              checked={form.isPrivate}
              onCheckedChange={(c) => setForm({ ...form, isPrivate: c })}
              data-testid="switch-edit-private-league"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => history.back()}
              data-testid="button-cancel-edit-league"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={update.isPending || !form.name.trim()}
              data-testid="button-save-edit-league"
            >
              {update.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Guardar cambios"
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
